import { QuizQuestionProps } from "@/pages/quiz/context/types";
import { Button, ButtonType } from "@/components/common/button";
import { useMemo, useState } from "react";
import { CSSTransition } from "react-transition-group";
import { useQuizContext } from "@/pages/quiz/context";
import { HeightWeightAnswer } from "@/pages/quiz/base/questions/heightWeightInputsWithBMI";
import { sanitizeDecimal } from "@/utils";
import { validateWeight } from "@/utils/validateField/weight";

const CustomNumberInputQuestion = ({
  question,
  questionValue,
  setQuestionAnswer,
  setQuestionValue,
}: QuizQuestionProps<string>) => {
  const { answers } = useQuizContext();
  const [errorMessage, setErrorMessage] = useState("");

  const {
    name,
    attributes: { inputType = "number", placeholder },
  } = question;

  const questionNamesWithLowerWeight = ["app-weight-goal", "core-weight-goal"];
  const isLowerWeightQuestion = questionNamesWithLowerWeight.includes(name);

  const weightQuestion = useMemo(
    () =>
      Object.values(answers).find(
        (answer) =>
          answer.blockName === "height-weight-input-fields" ||
          answer.blockName === "height-weight-input-fields-with-bmi"
      )?.value as HeightWeightAnswer,
    [answers]
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuestionValue(sanitizeDecimal(e.target.value));
    setErrorMessage("");
  };

  const onSubmitAnswer = () => {
    const error = validateWeight({
      currentWeight: +weightQuestion?.weight,
      value: +questionValue,
      isLowerWeightQuestion,
    });

    if (error) {
      setErrorMessage(error);
      return;
    }

    setErrorMessage("");
    setQuestionAnswer(questionValue);
  };

  return (
    <div className="relative">
      <input
        value={questionValue}
        type={inputType as string}
        placeholder={typeof placeholder === "string" ? placeholder : ""}
        inputMode="decimal"
        onChange={handleChange}
        className={`
          ${questionValue ? "border-medium-green" : "border-purple"}
          text-lg sm:text-xl lg:text-3xl pb-2 sm:pb-3 lg:pb-4 border-b-4 w-full outline-none text-center placeholder:text-cool-gray transition duration-500
          `}
      />
      <CSSTransition
        in={!!errorMessage}
        timeout={300}
        classNames="fade"
        unmountOnExit
      >
        <div className="mt-4 text-brick-red font-medium text-sm sm:text-base first-letter:capitalize">
          {errorMessage}
        </div>
      </CSSTransition>
      <Button
        type={ButtonType.NEXT}
        disabled={!questionValue.trim() || !!errorMessage}
        onClick={onSubmitAnswer}
      />
    </div>
  );
};

export { CustomNumberInputQuestion };
